// eslint-disable-next-line no-unused-vars
import {Brick, BrickContext, ErrorFlow, Order, Predicate, Query, registerBrick} from 'olympe';
import {Material} from '../../models/Material';
import {addNBusinessDays} from '../date';
import {PurchaseRequest} from '../../models/PurchaseRequest';
import {createPRJson} from '../createPRJson';
import {AccountDetailsByMaterial} from '../../models/financial_infos/AccountDetailsByMaterial';
import {cleanEmailList, mergeEmailsList} from '../email';
import {parseAccounts, parseLabelValue} from '../parseAPI';
import {checkPriceDataFromAPI} from '../helpers';
import {SOURCES, SOURCES_TO_DISPLAY} from '../constants';
import PurchaseRequestDM from '../../models/cloudobjects/PurchaseRequestDM';
import {checkLabelValueObject} from '../checkObjects';
import {convertSourceToLabelValue, determinePRSource, getMaterialSource} from '../sourceMappings';

/**
 * An action which tries to group a new purchase request with an existing one
 */
export default class TryToGroupPR extends Brick {
    /**
     * @param {object} parsedNewRequest
     * @param {boolean} fromDYN
     * @param {object} userDefaults
     * @return {Promise<QueryResult<PurchaseRequestDM> | undefined>}
     */
    async checkPRMatchingQuery(parsedNewRequest, fromDYN, userDefaults) {
        // There's no grouping for Fairmarkit and Agency Mania requests
        if (parsedNewRequest.sourceToDisplay === SOURCES_TO_DISPLAY.FAIRMARKIT ||
            parsedNewRequest.source === SOURCES.AGENCY_MANIA ||
            parsedNewRequest.source === SOURCES.TSF) {
            return Promise.resolve(undefined);
        }

        const newPurchaseOrg = (parsedNewRequest.purchaseOrg?.code || parsedNewRequest.purchaseOrg?.value) ||
            parsedNewRequest.headerDetails?.purchaseOrganization?.value ||
            userDefaults?.purchase_organization?.split('-')?.[0]?.trim() || '';

        const userDefaultsCompanyCode = userDefaults.company_code?.split('-')?.[0]?.trim() ?? '';

        const newSpender = fromDYN ?
            parsedNewRequest.spender?.value?.toUpperCase() :
            parsedNewRequest.username?.toUpperCase();

        const newVendor = fromDYN ?
            parsedNewRequest.vendorCode :
            (parsedNewRequest.childSupplierCode || parsedNewRequest.supplierCode);

        const groupingRequest = Query
            .instancesOf(PurchaseRequestDM)
            .filter(Predicate.and(
                Predicate.equals(PurchaseRequestDM.spenderUserIdProp, newSpender),
                Predicate.equals(PurchaseRequestDM.supplierCodeProp, newVendor?.toString() || ''),
                Predicate.not(Predicate.equals(PurchaseRequestDM.statusProp, PurchaseRequest.statuses.pendingNbs)),
                Predicate.not(Predicate.equals(PurchaseRequestDM.statusProp, PurchaseRequest.statuses.pendingSB)),
                Predicate.not(Predicate.equals(PurchaseRequestDM.statusProp, PurchaseRequest.statuses.submitted)),
                Predicate.not(Predicate.equals(PurchaseRequestDM.statusProp, PurchaseRequest.statuses.incompleteRequester)),
                Predicate.equals(PurchaseRequestDM.purchaseOrgValueProp, newPurchaseOrg),
            ));

        // If we try to group PR from DYN, we need to copy the company code to the "companyCodes" field
        if (fromDYN) {
            parsedNewRequest.companyCodes = [parsedNewRequest.headerDetails.companyCode.value];
        }

        let companyCodeFilteredGroupingRequest = groupingRequest;
        if (!parsedNewRequest.companyCodes || parsedNewRequest.companyCodes?.length === 0) {
            return Promise.resolve(undefined);
        }
        if (fromDYN || parsedNewRequest.companyCodes?.length === 1) {
            const newRequestCompanyCode = fromDYN ?
                parsedNewRequest.headerDetails.companyCode?.value :
                (parsedNewRequest.companyCodes[0].code || parsedNewRequest.companyCodes[0].value);

            if (newRequestCompanyCode === userDefaultsCompanyCode) {
                companyCodeFilteredGroupingRequest = groupingRequest.filter(
                    Predicate.or(
                        Predicate.equals(PurchaseRequestDM.companyCodeValueProp, null),
                        Predicate.equals(PurchaseRequestDM.companyCodeValueProp, ''),
                        Predicate.equals(PurchaseRequestDM.companyCodeValueProp, newRequestCompanyCode),
                    ));
            } else {
                companyCodeFilteredGroupingRequest = groupingRequest.filter(
                    Predicate.equals(PurchaseRequestDM.companyCodeValueProp, newRequestCompanyCode));
            }
        } else if (parsedNewRequest.companyCodes?.length > 1) {
            const codes = parsedNewRequest.companyCodes
                .map((cc) => cc?.code || cc?.value)
                .filter(Boolean);

            companyCodeFilteredGroupingRequest = groupingRequest.filter(
                Predicate.or(
                    Predicate.equals(PurchaseRequestDM.companyCodeValueProp, null),
                    Predicate.equals(PurchaseRequestDM.companyCodeValueProp, ''),
                    ...codes.map((c) => Predicate.equals(PurchaseRequestDM.companyCodeValueProp, c)),
                ),
            );
        }

        return companyCodeFilteredGroupingRequest.sortBy(PurchaseRequestDM.epridProp, Order.DESC).limit(1).execute();
    }

    /**
     * @param {object} parsedNewRequest
     * @return {Promise<QueryResult<PurchaseRequestDM> | undefined>}
     */
    async checkPRMatchingBySourcingEventIDQuery(parsedNewRequest) {
        // There's no grouping for Fairmarkit and Agency Mania requests
        if (parsedNewRequest.sourceToDisplay === SOURCES_TO_DISPLAY.FAIRMARKIT ||
            parsedNewRequest.source === SOURCES.AGENCY_MANIA ||
            parsedNewRequest.source === SOURCES.TSF) {
            return Promise.resolve(undefined);
        }

        let newSourcingEventID;
        if (Array.isArray(parsedNewRequest.materials)) { // If API V3
            newSourcingEventID = parsedNewRequest.materials[0].sourcingEventID;
        } else {
            newSourcingEventID = parsedNewRequest.sourcingEventID;
        }

        if (!newSourcingEventID) {
            return Promise.resolve(undefined);
        }

        const query = Query.instancesOf(PurchaseRequestDM)
            .filter(Predicate.and(
                Predicate.equals(PurchaseRequestDM.sourcingEventIdProp, newSourcingEventID),
                Predicate.equals(PurchaseRequestDM.sourceProp, SOURCES.TSF),
                Predicate.not(Predicate.equals(PurchaseRequestDM.statusProp, PurchaseRequest.statuses.submitted)),
            ));

        return query.execute();
    }

    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {string} newPurchaseRequest
     * @param {!Object} userDefaults
     * @param {boolean} fromDYN
     * @param {function()} forwardEvent
     * @param {function(boolean)} setGroupingSuccessful
     * @param {function(string)} setRequest
     * @param {function(string)} setTag
     */
    async update($, [newPurchaseRequest, userDefaults, fromDYN], [forwardEvent, setGroupingSuccessful, setRequest, setTag, setErrorFlow]) {
        try {
            try {
                const parsedNewRequest = JSON.parse(newPurchaseRequest);

                let groupingRequest;
                groupingRequest = (await this.checkPRMatchingBySourcingEventIDQuery(parsedNewRequest))?.toArray()[0];
                if (!groupingRequest) {
                    groupingRequest = (await this.checkPRMatchingQuery(parsedNewRequest, fromDYN, userDefaults))?.toArray()[0];
                }

                if (groupingRequest) {
                    const tmpPR = new PurchaseRequest(groupingRequest.getJson());
                    const sameSource =
                            (parsedNewRequest.sourceFromApiKey && tmpPR.sourceFromApiKey) ?
                                parsedNewRequest.sourceFromApiKey === tmpPR.sourceFromApiKey :
                                parsedNewRequest.source === tmpPR.source;

                    if (!sameSource) {
                        groupingRequest = undefined;
                    }
                }

                if (groupingRequest) {
                    groupingRequest = {
                        purchaseRequest: new PurchaseRequest(groupingRequest.getJson()),
                        tag: groupingRequest.getTag(),
                    };
                }

                if (groupingRequest) {
                    // Add the material to the request that accept to group
                    const material = fromDYN ? parsedNewRequest.materials.map((material, i) => { // DYN
                        return new Material(
                            groupingRequest.purchaseRequest.materials.length + i,
                            parsedNewRequest.materials[0].name,
                            parsedNewRequest.materials[0].description,
                            parsedNewRequest.materials[0].quantity,
                            parsedNewRequest.materials[0].unit,
                            parsedNewRequest.materials[0].baseUnit,
                            parsedNewRequest.materials[0].price,
                            parsedNewRequest.materials[0].currency,
                            parsedNewRequest.materials[0].per,
                            parsedNewRequest.materials[0].taxCode,
                            parsedNewRequest.materials[0].tax,
                            parsedNewRequest.materials[0].l1Category,
                            parsedNewRequest.materials[0].l3Category,
                            parsedNewRequest.materials[0].requestedDeliveryDate,
                            parsedNewRequest.materials[0].plant,
                            parsedNewRequest.materials[0].storageLocation,
                            parsedNewRequest.materials[0].deliveryAddress,
                            parsedNewRequest.materials[0].total,
                            parsedNewRequest.materials[0].freeItem,
                            parsedNewRequest.materials[0].transportCosts,
                            parsedNewRequest.materials[0].supplierPartId,
                            parsedNewRequest.materials[0].supplierPartAuxiliaryId,
                            parsedNewRequest.materials[0].arePriceDataFromST,
                            parsedNewRequest.materials[0].supplierComments,
                            parsedNewRequest.materials[0].internalComments,
                            parsedNewRequest.materials[0]?.exchangeCurrencyRate,
                            parsedNewRequest.materials[0]?.type,
                            parsedNewRequest.materials[0]?.supplierStatus,
                            parsedNewRequest.materials[0]?.pslId,
                            parsedNewRequest.materials[0]?.transportCostType,
                            parsedNewRequest.materials[0]?.transportCostConditions,
                            parsedNewRequest.materials[0]?.pslMaterialId,
                            parsedNewRequest.materials[0]?.ppmCostPlan,
                            parsedNewRequest.materials[0]?.taxAmount,
                            convertSourceToLabelValue(parsedNewRequest.materials[0]?.pslId ? 'PSL' : 'NCT'), // NCT is the default source for DYN
                        );
                    }) :
                        Array.isArray(parsedNewRequest?.materials) ? parsedNewRequest.materials.map((material, i) => { // API V3
                            const materialSource = getMaterialSource(parsedNewRequest.source, parsedNewRequest.sourceFromApiKey);
                            return new Material(
                                groupingRequest.purchaseRequest.materials.length + i,
                                material.materialNumber + ' - ' + material.materialName,
                                material.shortDescription,
                                groupingRequest.purchaseRequest.wayOfBuying.currentValue.value === 'FO' ? '1' : (material.quantity?.toString() ?? ''),
                                material.unitOfMeasure,
                                material.baseUnit,
                                material.unitPrice,
                                material.currencyCode,
                                material.pricePer || 1,
                                checkLabelValueObject(material.taxCode),
                                '',
                                material.l1Code + ' - ' + material.l1Description,
                                material.l3Code + ' - ' + material.l3Description,
                                addNBusinessDays(new Date(), parsedNewRequest.supplierLeadTime || 5).toDateString(),
                                parseLabelValue(parsedNewRequest.plant, groupingRequest.purchaseRequest.headerDetails.defaultPlant),
                                groupingRequest.purchaseRequest.headerDetails.defaultStorageLocation,
                                groupingRequest.purchaseRequest.headerDetails.defaultDeliveryAddress,
                                '',
                                material.freeItem,
                                false,
                                material.supplierPartId,
                                material.supplierPartAuxiliaryId,
                                checkPriceDataFromAPI(material.unitOfMeasure, material.unitPrice, material.currencyCode, material.pricePer),
                                material.supplierComment,
                                material.internalComment,
                                1.0,
                                material.type,
                                material.statusName || groupingRequest.purchaseRequest.headerDetails.supplierStatus || 'Blank',
                                material.externalItemId,
                                material.transportCostType,
                                material.transportCostConditions,
                                material.externalItemUniqueId,
                                material.ppmCostPlan,
                                material.taxAmount,
                                materialSource,
                            );
                        }) : // API V2.1
                            (() => {
                                const materialSource = getMaterialSource(parsedNewRequest.source, parsedNewRequest.sourceFromApiKey);
                                return [new Material(
                                    groupingRequest.purchaseRequest.materials.length,
                                    parsedNewRequest.materialNumber + ' - ' + parsedNewRequest.materialName,
                                    parsedNewRequest.shortDescription,
                                    groupingRequest.purchaseRequest.wayOfBuying.currentValue.value === 'FO' ? '1' : (parsedNewRequest.quantity?.toString() ?? ''),
                                    parsedNewRequest.unitOfMeasure,
                                    parsedNewRequest.baseUnit,
                                    parsedNewRequest.unitPrice,
                                    parsedNewRequest.currencyCode,
                                    parsedNewRequest.pricePer || 1,
                                    {label: '', value: ''},
                                    '',
                                    parsedNewRequest.spendCat1Code + ' - ' + parsedNewRequest.spendCat1DisplayValue,
                                    parsedNewRequest.spendCat3Code + ' - ' + parsedNewRequest.spendCat3DisplayValue,
                                    addNBusinessDays(new Date(), parsedNewRequest.supplierLeadTime || 5).toDateString(),
                                    parseLabelValue(parsedNewRequest.plant, groupingRequest.purchaseRequest.headerDetails.defaultPlant),
                                    groupingRequest.purchaseRequest.headerDetails.defaultStorageLocation,
                                    groupingRequest.purchaseRequest.headerDetails.defaultDeliveryAddress,
                                    '',
                                    false,
                                    false,
                                    parsedNewRequest.supplierPartId,
                                    parsedNewRequest.supplierPartAuxiliaryId,
                                    checkPriceDataFromAPI(parsedNewRequest.unitOfMeasure, parsedNewRequest.unitPrice, parsedNewRequest.currencyCode, parsedNewRequest.pricePer),
                                    '',
                                    '',
                                    1.0,
                                    parsedNewRequest.type,
                                    parsedNewRequest.statusName,
                                    parsedNewRequest.pslId,
                                    parsedNewRequest.transportCostType,
                                    parsedNewRequest.transportCostConditions,
                                    parsedNewRequest.pslMaterialId,
                                    parsedNewRequest.ppmCostPlan,
                                    parsedNewRequest.taxAmount,
                                    materialSource,
                                )];
                            })();

                    // add the new material before the transport costs, if any
                    const transportCostMaterial = PurchaseRequest.getTransportsCostsMaterial(groupingRequest.purchaseRequest.materials);
                    if (transportCostMaterial !== null) {
                        const newIndex = groupingRequest.purchaseRequest.materials.length - 1;
                        material.id = newIndex;
                        groupingRequest.purchaseRequest.materials.splice(newIndex, 0, ...material); // insert at element at newIndex
                        transportCostMaterial.id++;
                    } else {
                        groupingRequest.purchaseRequest.materials.push(...material);
                    }

                    // Add the material financial info if the PR does split account
                    if (groupingRequest.purchaseRequest.financialInfo.areAccountSplitByMaterial) {
                        const accountDetails = Array.isArray(material) ? material.map((item) => {
                            return new AccountDetailsByMaterial(
                                item,
                                parseAccounts(parsedNewRequest.accountDetails),
                            );
                        }) : [new AccountDetailsByMaterial(
                            material,
                            parseAccounts(parsedNewRequest.accountDetails),
                        )];

                        // add the new account details before the transport costs, if any
                        if (transportCostMaterial !== null) {
                            const newIndex = groupingRequest.purchaseRequest.financialInfo.accountDetailsByMaterial.length - 1;
                            groupingRequest.purchaseRequest.financialInfo.accountDetailsByMaterial.splice(newIndex, 0, ...accountDetails); // insert at element at newIndex
                        } else {
                            groupingRequest.purchaseRequest.financialInfo.accountDetailsByMaterial.push(...accountDetails);
                        }
                    }

                    // Recalculate PR source based on all materials (including newly added ones)
                    const allMaterialSources = groupingRequest.purchaseRequest.materials.map((mat) => mat.source);
                    groupingRequest.purchaseRequest.prSource = determinePRSource(allMaterialSources);

                    // Append the vendor email address if different from the previous ones
                    const newEmails = cleanEmailList([parsedNewRequest.vendorContactEmail]); // cleanEmailList checks that input is not null or is an empty string
                    groupingRequest.purchaseRequest.vendorContactEmails = mergeEmailsList(groupingRequest.purchaseRequest.vendorContactEmails, newEmails);

                    setGroupingSuccessful(true);
                    setRequest(createPRJson(groupingRequest.purchaseRequest));
                    setTag(groupingRequest.tag);
                } else {
                    setGroupingSuccessful(false);
                    setRequest(newPurchaseRequest);
                    setTag(undefined);
                }
                forwardEvent();
            } catch (e) {
                console.error(e);
                setErrorFlow(ErrorFlow.create('Error in Try to group', 1));
            }
        } catch (e) {
            console.error(e);
            setErrorFlow(ErrorFlow.create('Error in Try to group', 1));
        }
    }
}

registerBrick('01808a0c5b76950cf559', TryToGroupPR);
